/**
 * Central plan configuration for Stripe product-to-tier resolution.
 * Mirrors backend mapping hierarchy for consistency across services.
 */
import { captureException } from './telemetry/posthog/frontend';
import { IS_PROD } from './utils/env/is-prod';

/** Plan tiers resolvable from a Stripe product ID. */
export type PlanTier = 'free' | 'pro' | 'business';

// TODO: move product/tier mapping to proper runtime configuration so
// Stripe product IDs are not stored in source code.
const PRODUCT_TIER_MAP_BY_ENV: Record<string, Record<string, PlanTier>> = {
  production: {
    prod_UNMOM9KJqjxGQY: 'free',
    prod_UNMOJjFzoo7VMl: 'pro',
    prod_UNMOxoVcfJi87E: 'business',
  },
  staging: {
    prod_UDYPtYyYpriaq1: 'free',
    prod_UDZQz2zIHEnqds: 'pro',
    prod_UDZUdpUxUgGP5y: 'business',
  },
}

/** Unknown product ids already captured this page load, so render loops do not spam PostHog. */
const reportedUnknownPlanIds = new Set<string>();

/**
 * Resolves a Stripe product ID to a plan tier.
 *
 * Returns 'free' for null/undefined plan ids. Unknown non-null plan ids
 * (including production ids hitting the staging map, or vice versa) also
 * resolve to 'free' (fail-closed) and are captured to PostHog instead of
 * throwing, so a bad plan id can never unmount the app during render.
 */
export function resolvePlanTier(planId: string | null | undefined): PlanTier {
  if (!planId) return 'free';
  // VITE_ENVIRONMENT is a build-time var, so IS_PROD is fixed per build:
  // production picks the production map, everything else (staging, preview,
  // unset) falls through to the staging map.
  const env = IS_PROD ? 'production' : 'staging';
  const envMap = PRODUCT_TIER_MAP_BY_ENV[env] ?? PRODUCT_TIER_MAP_BY_ENV.staging;
  const tier = envMap[planId];
  if (!tier) {
    if (!reportedUnknownPlanIds.has(planId)) {
      reportedUnknownPlanIds.add(planId);
      captureException(
        new Error(`Unknown pricing plan product_id: ${planId}`),
        'planConfig.resolvePlanTier',
        { planId, environment: env },
      );
    }
    return 'free';
  }
  return tier;
}
